/* Lễ tân: tình trạng phòng, tư vấn viên, kỹ thuật viên trong ngày — để phân khách đã check-in. Logic thuần, dùng chung store App Tư vấn */
import type { Mang } from '../Telesales/lead/logic';
import {
  CONSULT_ROOMS, DOCTORS, EXEC_ROOMS, MIN, REASSIGN_MIN, TECHS, WAIT_WARN_MIN, consultantsOf, inExecRoom, isToday, techName,
  type Doctor, type Session, type StaffSt, type Tech, type Visit,
} from './logic';

/** Phòng và nhân sự của cơ sở: màn hình truyền danh sách server, không truyền thì lấy danh sách mẫu */
export interface BoardRefs {
  consultRooms: string[];
  execRooms: string[];
  consultants: { id: string; name: string; color: string; branchId: string }[];
  techs: Tech[];
  doctors: Doctor[];
}
const defaultRefs = (branchId: string): BoardRefs => ({
  consultRooms: CONSULT_ROOMS, execRooms: EXEC_ROOMS, consultants: consultantsOf(branchId), techs: TECHS.filter((t) => t.branchId === branchId), doctors: DOCTORS,
});

const ofDay = (visits: Visit[], branchId: string, now: number) =>
  visits.filter((v) => v.branchId === branchId && v.status !== 'cancelled' && isToday(v.checkInAt ?? v.createdAt, now));

export interface RoomSlot { name: string; kind: 'consult' | 'exec'; visit?: Visit; since?: number }

/**
 * Phòng tư vấn bận khi có khách đang tư vấn trong phòng, hoặc Lễ tân đã giữ phòng cho khách đang chờ tư vấn viên;
 * phòng thủ thuật bận khi có khách đang làm dịch vụ chưa xong.
 */
export function roomBoard(visits: Visit[], branchId: string, now: number, refs = defaultRefs(branchId)): RoomSlot[] {
  const vs = ofDay(visits, branchId, now);
  return [
    ...refs.consultRooms.map((name): RoomSlot => {
      const v = vs.find((x) => x.room === name && (x.status === 'consulting' || x.status === 'waiting'));
      return { name, kind: 'consult', visit: v, since: v ? v.startedAt ?? v.assignedAt : undefined };
    }),
    ...refs.execRooms.map((name): RoomSlot => {
      const v = vs.find((x) => x.exec?.room === name && !x.exec.doneAt);
      return { name, kind: 'exec', visit: v, since: v?.exec?.at };
    }),
  ];
}

/** free = sẵn sàng, chưa có khách · held = đã được phân khách đang chờ · busy = đang tư vấn */
export type ConsSt = 'free' | 'held' | 'busy' | 'break' | 'off';
export const CONS_ST: Record<ConsSt, { label: string; color: string }> = {
  free: { label: 'Trống', color: 'green' },
  held: { label: 'Đã phân khách', color: 'gold' },
  busy: { label: 'Đang tư vấn', color: 'orange' },
  break: { label: 'Tạm nghỉ', color: 'default' },
  off: { label: 'Ngoại tuyến', color: 'default' },
};
export interface ConsRow { id: string; name: string; color: string; st: ConsSt; visit?: Visit; held: Visit[]; served: number }

export function consultantBoard(visits: Visit[], branchId: string, staffSt: Record<string, StaffSt>, now: number, refs = defaultRefs(branchId)): ConsRow[] {
  const vs = ofDay(visits, branchId, now);
  return refs.consultants.map((c) => {
    const visit = vs.find((v) => v.consultant === c.id && v.status === 'consulting');
    const held = vs.filter((v) => v.consultant === c.id && v.status === 'waiting');
    const served = vs.filter((v) => v.consultant === c.id && v.status !== 'referral').length;
    const s = staffSt[c.id] ?? 'ready';
    const st: ConsSt = s !== 'ready' ? s : visit ? 'busy' : held.length ? 'held' : 'free';
    return { id: c.id, name: c.name, color: c.color, st, visit, held, served };
  });
}

export interface TechRow { tech: Tech; visit?: Visit; served: number }

export function techBoard(visits: Visit[], branchId: string, now: number, refs = defaultRefs(branchId)): TechRow[] {
  const vs = ofDay(visits, branchId, now);
  return refs.techs.map((tech) => ({
    tech,
    visit: vs.find((v) => v.exec?.tech === tech.id && !v.exec.doneAt),
    served: vs.filter((v) => v.exec?.tech === tech.id).length,
  }));
}

/** Gợi ý tư vấn viên: trống trước, rồi đã phân ít khách; cùng mức thì ít lượt trong ngày */
export function suggestConsultant(rows: ConsRow[]): ConsRow | undefined {
  const rank: Partial<Record<ConsSt, number>> = { free: 0, held: 1, busy: 2 };
  return rows.filter((r) => rank[r.st] !== undefined)
    .sort((a, b) => rank[a.st]! - rank[b.st]! || a.held.length - b.held.length || a.served - b.served)[0];
}

/** KTV trống, làm được mảng của khách xếp trước; cùng mức thì ít lượt trong ngày */
export function suggestTech(rows: TechRow[], mang?: Mang): TechRow | undefined {
  const miss = (r: TechRow) => (mang && !r.tech.mangs.includes(mang) ? 1 : 0);
  return rows.filter((r) => !r.visit).sort((a, b) => miss(a) - miss(b) || a.served - b.served)[0];
}

export const freeRooms = (slots: RoomSlot[], kind: RoomSlot['kind'], mine?: Visit) =>
  slots.filter((s) => s.kind === kind && (!s.visit || s.visit.id === mine?.id)).map((s) => s.name);

/* ---------- Bác sĩ ---------- */

/** Bác sĩ được mời khám coi như bận tối đa N phút, hoặc tới khi ký khám trên phiếu */
export const DOCTOR_MIN = 30;
const examEnd = (v: Visit) => {
  const signed = v.quote?.exam.signedAt;
  return signed && signed >= v.invite!.at ? signed : v.invite!.at + DOCTOR_MIN * MIN;
};
export interface DocRow { id: string; name: string; visit?: Visit; why?: 'exam' | 'exec'; since?: number; served: number }

/** Bác sĩ bận: đang khám cho khách tư vấn (được mời, chưa ký khám) hoặc đang làm dịch vụ cùng khách */
export function doctorBoard(visits: Visit[], branchId: string, now: number, refs = defaultRefs(branchId)): DocRow[] {
  const vs = ofDay(visits, branchId, now);
  return refs.doctors.map((d) => {
    const ex = vs.find((v) => v.exec?.doctor === d.id && !v.exec.doneAt);
    const inv = vs.find((v) => v.status === 'consulting' && v.invite?.doctor === d.id && v.invite.at <= now && now < examEnd(v));
    const served = vs.filter((v) => v.invite?.doctor === d.id || v.exec?.doctor === d.id).length;
    if (ex) return { id: d.id, name: d.name, visit: ex, why: 'exec' as const, since: ex.exec!.at, served };
    if (inv) return { id: d.id, name: d.name, visit: inv, why: 'exam' as const, since: inv.invite!.at, served };
    return { id: d.id, name: d.name, served };
  });
}

/* ---------- Buổi làm dịch vụ hẹn hôm nay (App Tư vấn đã xếp) ---------- */

export interface TodaySession { plan: string; line: string; s: Session }
/** Buổi đã xếp trong ngày của lượt (chưa huỷ) — Lễ tân xếp phòng / KTV theo đây */
export const todaySessions = (v: Visit, now: number): TodaySession[] => v.plans
  .filter((p) => !p.cancelledAt)
  .flatMap((p) => p.sessions.filter((s) => s.status === 'scheduled' && s.at && isToday(s.at, now)).map((s) => ({ plan: p.id, line: p.line, s })));

/* ---------- Cảnh báo cho Lễ tân ---------- */

export type Warn = 'wait' | 'notTaken' | 'execDue';
/** Chờ quá lâu chưa ai nhận · đã phân quá REASSIGN_MIN phút tư vấn viên chưa nhận · đến giờ buổi làm DV chưa xếp phòng */
export function warnsOf(v: Visit, now: number): Warn[] {
  const out: Warn[] = [];
  // Khách Hàng chờ thực hiện (đã có người phụ trách làm DV) không cần phân tư vấn viên
  if (v.status === 'waiting' && !v.consultant && !v.execOwner && !inExecRoom(v) && now - (v.checkInAt ?? now) >= WAIT_WARN_MIN * MIN) out.push('wait');
  if (v.status === 'waiting' && v.consultant && now - (v.assignedAt ?? now) >= REASSIGN_MIN * MIN) out.push('notTaken');
  if (!v.exec && todaySessions(v, now).some((x) => x.s.at! <= now)) out.push('execDue');
  return out;
}
export const WARN_TEXT: Record<Warn, string> = {
  wait: `Chờ quá ${WAIT_WARN_MIN} phút chưa phân tư vấn`,
  notTaken: `Tư vấn viên chưa nhận sau ${REASSIGN_MIN} phút`,
  execDue: 'Đến giờ làm dịch vụ, chưa xếp phòng',
};

/* ---------- Chia tự động có giữ phòng ---------- */

export interface AutoPick { id: string; consultant: string; room?: string }
/**
 * Chia lần lượt khách chờ chưa có người (check-in sớm trước): tư vấn viên gợi ý + phòng tư vấn trống;
 * mỗi lượt chia xong tính lại bảng để không dồn hai khách cho cùng một người / một phòng.
 */
export function planAuto(visits: Visit[], branchId: string, staffSt: Record<string, StaffSt>, now: number, refs = defaultRefs(branchId)): AutoPick[] {
  let pool = visits;
  const out: AutoPick[] = [];
  ofDay(visits, branchId, now).filter((v) => v.status === 'waiting' && !v.consultant && !v.execOwner && !inExecRoom(v))
    .sort((a, b) => (a.checkInAt ?? 0) - (b.checkInAt ?? 0))
    .forEach((v) => {
      const c = suggestConsultant(consultantBoard(pool, branchId, staffSt, now, refs));
      if (!c) return;
      const room = v.room ?? freeRooms(roomBoard(pool, branchId, now, refs), 'consult')[0];
      out.push({ id: v.id, consultant: c.id, room });
      pool = pool.map((x) => (x.id === v.id ? { ...x, consultant: c.id, room, assignedAt: now } : x));
    });
  return out;
}

/* ---------- Lịch theo giờ ---------- */

export type BlockTone = 'held' | 'consult' | 'done' | 'exam' | 'exec' | 'plan';
export interface TBlock { from: number; to: number; label: string; tone: BlockTone; visitId: string }
export interface TRow { id: string; name: string; sub?: string; blocks: TBlock[] }
export type TimelineKind = 'cons' | 'doctor' | 'tech' | 'room';

/** Lượt đã xong mà chưa ghi giờ kết quả thì coi như tư vấn 30 phút */
const consultEnd = (v: Visit, now: number) => (v.status === 'consulting' ? now : v.result?.at ?? (v.startedAt ?? now) + 30 * MIN);

/** Các khối thời gian trong ngày theo từng người / phòng: chờ nhận, đang tư vấn, bác sĩ khám, làm dịch vụ, buổi đã hẹn */
export function timeline(visits: Visit[], branchId: string, now: number, kind: TimelineKind, refs = defaultRefs(branchId)): TRow[] {
  const vs = ofDay(visits, branchId, now);
  const consultBlocks = (v: Visit): TBlock[] => [
    ...(v.assignedAt && (v.status === 'waiting' || (v.startedAt && v.startedAt - v.assignedAt >= MIN))
      ? [{ from: v.assignedAt, to: v.status === 'waiting' ? now : v.startedAt!, label: `${v.cust.name} · chờ nhận`, tone: 'held' as const, visitId: v.id }] : []),
    ...(v.startedAt ? [{ from: v.startedAt, to: consultEnd(v, now), label: v.cust.name, tone: (v.status === 'consulting' ? 'consult' : 'done') as BlockTone, visitId: v.id }] : []),
  ];
  const execBlock = (v: Visit): TBlock[] => (v.exec ? [{ from: v.exec.at, to: v.exec.doneAt ?? now, label: `${v.cust.name} · làm DV`, tone: 'exec', visitId: v.id }] : []);
  const planBlocks = (v: Visit, hit: (s: Session) => boolean): TBlock[] => (v.exec ? [] : todaySessions(v, now)
    .filter((x) => hit(x.s))
    .map((x) => ({ from: x.s.at!, to: x.s.at! + 60 * MIN, label: `${v.cust.name} · hẹn làm DV`, tone: 'plan' as const, visitId: v.id })));

  if (kind === 'cons') {
    return refs.consultants.map((c) => ({ id: c.id, name: c.name, blocks: vs.filter((v) => v.consultant === c.id).flatMap(consultBlocks) }));
  }
  if (kind === 'doctor') {
    return refs.doctors.map((d) => ({
      id: d.id,
      name: d.name,
      blocks: vs.flatMap((v) => [
        ...(v.invite?.doctor === d.id
          ? [{ from: v.invite.at, to: Math.min(examEnd(v), v.status === 'consulting' ? Math.max(now, v.invite.at) : Infinity), label: `${v.cust.name} · khám`, tone: 'exam' as const, visitId: v.id }]
          : []),
        ...(v.exec?.doctor === d.id ? execBlock(v) : []),
        ...planBlocks(v, (s) => s.doctor === d.id),
      ]),
    }));
  }
  if (kind === 'tech') {
    return refs.techs.map((t) => ({
      id: t.id, name: techName(t.id), sub: t.mangs.join(' · '), blocks: vs.filter((v) => v.exec?.tech === t.id).flatMap(execBlock),
    }));
  }
  return [
    ...refs.consultRooms.map((r) => ({ id: r, name: r, sub: 'Tư vấn', blocks: vs.filter((v) => v.room === r).flatMap(consultBlocks) })),
    ...refs.execRooms.map((r) => ({
      id: r, name: r, sub: 'Thủ thuật',
      blocks: vs.flatMap((v) => [...(v.exec?.room === r ? execBlock(v) : []), ...planBlocks(v, (s) => s.room === r)]),
    })),
  ];
}
